// packages/components/src/doctor-ai/DocumentsTab.tsx
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUp } from 'lucide-react';
import { Checkbox } from '@dopomogai/ui'; // Now we can import our new component
import { UploadedFileListItem } from '../../platform/platform_features/agent-builder/components/UploadedFileListItem'; // The CORRECT component for a list item

interface Document {
  id: string;
  file: { name: string; size: number };
  status: 'ready' | 'error' | 'processed';
}

interface DocumentsTabProps {
  documents: Document[];
  onFileAdd: (files: File[]) => void;
  onFileRemove: (fileId: string) => void;
  selectedFileIds: Set<string>;
  onFileSelectionChange: (fileId: string, isSelected: boolean) => void;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  onFileAdd,
  onFileRemove,
  selectedFileIds,
  onFileSelectionChange,
}) => {
  const { t } = useTranslation('agentBuilder');
  
  // NOTE: The dropzone itself is now just a visual placeholder in this context,
  // as file uploads are initiated from the ChatConsole's paperclip icon.
  const dropzoneText = t('fileUpload.fileTypesAndSizeHint', { types: 'PDF, TXT', maxSize: 10 });

  return (
    <div className="space-y-4">
      {/* Visual-only Dropzone */}
      <div className="p-6 border-2 border-dashed rounded-xl text-center border-slate-300">
        <div className="flex flex-col items-center justify-center">
            <FileUp size={32} className="text-slate-400 mb-2" />
            <p className="text-sm font-semibold text-slate-700">Attach Files in Chat</p>
            <p className="text-xs text-slate-500 mt-1">{dropzoneText}</p>
        </div>
      </div>

      {/* Uploaded Files List with RAG Checkboxes */}
      {documents.length > 0 && (
        <div className="space-y-2">
            <h4 className="text-sm font-bold text-slate-800">{t('fileUpload.uploadedFilesTitle', { count: documents.length })}</h4>
            {documents.map(doc => (
                 <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100">
                    <Checkbox
                        id={`file-select-${doc.id}`}
                        checked={selectedFileIds.has(doc.id)}
                        // FIX: The onCheckedChange prop expects a function that takes a boolean.
                        onCheckedChange={(checked) => onFileSelectionChange(doc.id, !!checked)}
                        aria-label={`Select file ${doc.file.name}`}
                    />
                    <div className="flex-grow">
                        {/* 
                          FIX: Using the correct component here.
                          The 'as any' cast is a pragmatic choice for the demo because UploadedFileListItem
                          expects a richer 'UploadableFile' type. This bridges the gap without a major refactor.
                        */}
                        <UploadedFileListItem fileData={doc as any} onRemove={onFileRemove} />
                    </div>
                </div>
            ))}
        </div>
      )}
    </div>
  );
};
